import { Badge, Button, Icon, Link, Stack, Text } from "@chakra-ui/react"
import { createColumnHelper } from "@tanstack/react-table"
import { ConfirmationDialog } from "components/confirmation/confirmation"
import { ActionDrawer } from "components/drawer"
import { defaultColumns } from "components/table/defaultColumns"
import { removeBroadcast } from "lib/firebase"
import { remove } from "lib/firebase"
import { formatScheduleDate, formatSeconds } from "lib/utils"
import LinkNames from "components/table/LinkNames"
import { isCompositeBroadcast } from "lib/playlist"
import BroadcastLinks from "views/modal/broadcast-links"
import moment from "moment"
import { MdClose } from "react-icons/md"
import BroadcastModal from "views/modal/broadcast"

const helper = createColumnHelper()

// Where the broadcast is used: a button with the names, which opens the list to change it.
// `list` = [{ id, name, count? }] from the table context (see lib/membership.js#broadcastLinks)
function LinksCell({ broadcast, kind, list, none, title, onChange }) {
  const names = list.map(item => item.count > 1 ? `${item.name} (x${item.count})` : item.name)
  return (
    <ActionDrawer
      title={`${title}: ${broadcast.name}`}
      size='xl'
      trigger={<LinkNames names={names} none={none} />}
      >
      <BroadcastLinks broadcast={broadcast} kind={kind} onChange={onChange} />
    </ActionDrawer>
  )
}

// The text the search looks at: all names, not only the few the cell shows
const linksSearchText = (list) => list.length > 0 ? list.map(item => item.name).join(' ') : 'None'

export const columnsBroadcasts = [

  helper.display({
    id: 'type',
    size: 100,
    header: 'Type',
    meta: { searchText: item => item.type === 'regular' ? 'Regular' : 'Composite' },
    cell: props => (
      props.row.original.type === 'regular' ? (
        <Badge fontSize='0.7em' colorScheme='blue'>Regular</Badge>
      ) : (
        <Badge fontSize='0.7em' colorScheme='purple'>Composite</Badge>
      )
    ),
  }),
  ...defaultColumns,
  helper.display({
    id: 'items',
    size: 100,
    header: 'Items',
    meta: { searchText: item => String(item.contentList ? (item.contentList.length || 0) : (item.broadcastList?.length || 0)) },
    cell: props => (
      props.row.original.contentList ? (
        <Text>{props.row.original.contentList?.length || 0}</Text>
      ) : (
        <Text>{props.row.original.broadcastList?.length || 0}</Text>
      )
    ),
  }),
  helper.accessor('duration', {
    size: 100,
    header: 'Duration',
    meta: { searchText: item => formatSeconds(item.duration) },
    cell: props => (
      <Text>
        {formatSeconds(props.getValue())}
      </Text>
    ),
  }),
  helper.display({
    id: 'groups',
    size: 240,
    header: 'Groups',
    meta: { searchText: (item, context) => linksSearchText(context?.links?.groups.get(item.id) || []) },
    cell: props => (
      <LinksCell
        broadcast={props.row.original}
        kind='group'
        title='Groups playing'
        none='Not played by any group. Click to add it to groups.'
        list={props.table.options.context?.links?.groups.get(props.row.original.id) || []}
        onChange={props.table.options.context?.refresh}
      />
    ),
  }),
  helper.display({
    id: 'locations',
    size: 240,
    header: 'Locations',
    meta: { searchText: (item, context) => linksSearchText(context?.links?.locations.get(item.id) || []) },
    cell: props => (
      <LinksCell
        broadcast={props.row.original}
        kind='location'
        title='Locations playing'
        none='Not set on any location directly. Click to add it to locations.'
        list={props.table.options.context?.links?.locations.get(props.row.original.id) || []}
        onChange={props.table.options.context?.refresh}
      />
    ),
  }),
  // Only a regular broadcast can be part of a composite one
  helper.display({
    id: 'composites',
    size: 240,
    header: 'In composite',
    meta: {
      searchText: (item, context) => isCompositeBroadcast(item) ? 'N/A' : linksSearchText(context?.links?.composites.get(item.id) || []),
    },
    cell: props => isCompositeBroadcast(props.row.original) ? (
      <Text>N/A</Text>
    ) : (
      <LinksCell
        broadcast={props.row.original}
        kind='composite'
        title='Composite broadcasts of'
        none='Not in any composite broadcast. Click to add it.'
        list={props.table.options.context?.links?.composites.get(props.row.original.id) || []}
        onChange={props.table.options.context?.refresh}
      />
    ),
  }),
  helper.accessor('schedule', {
    size: 100,
    meta: { searchText: item => item.schedule ? formatScheduleDate(item.schedule.date) : '' },
    cell: props => (
      props.getValue() ? (
        <Badge fontSize='0.7em' colorScheme='green'>{formatScheduleDate(props.getValue().date)}</Badge>
      ) : (
        <Icon as={MdClose} width="12px" height="12px" color="inherit" />
      )
    ),
  }),
  helper.display({
    id: 'chyron',
    size: 100,
    header: 'Chyron',
    meta: { searchText: item => item.showChyron ? 'Yes' : 'No' },
    cell: props => (
      props.row.original.showChyron ? (
        <Badge fontSize='0.7em' colorScheme='green'>Yes</Badge>
      ) : (
        <Badge fontSize='0.7em' colorScheme='red'>No</Badge>
      )
    ),
  }),
  helper.accessor('updated_at', {
    size: 200,
    header: 'Last update',
    meta: { searchText: item => item.updated_at ? moment(item.updated_at).format('LLL') : 'Long time ago...' },
    cell: props => (
      <Text>
        {props.getValue() ? moment(props.getValue()).format('LLL'): 'Long time ago...'}
      </Text>
    ),
  }),
  helper.display({
    id: 'actions',
    header: 'Actions',
    size: 200,
    cell: props => (
      <Stack direction='row' spacing={2} align='center'>
        <ActionDrawer
          title='Update broadcast'
          size={'xxl'}
          trigger={
            <Button colorScheme='brand' variant='ghost' size='xs'>
              Update
            </Button>
          }
          >
          <BroadcastModal broadcast={props.row.original} onChange={props.table.options.context?.refresh} />
        </ActionDrawer>
        <ConfirmationDialog
          title='Delete broadcast'
          message='Are you sure you want to delete this broadcast?'
          confirmLabel='Delete'
          trigger={
            <Button colorScheme='red' variant='ghost' size='xs'>
              Delete
            </Button>
          }
          onConfirm={async () => {
            await removeBroadcast(props.row.original)
            props.table.options.context?.refresh()
          }}
          />
      </Stack>
    ),
  }),
]